import AstrologyCover from "@/assets/img/today/reports/book_covers_preview_Astrology_Report.webp"
import LoveMatchCover from "@/assets/img/today/reports/book_covers_preview_Love_Match_Report.webp"
import CompatibilityCover from "@/assets/img/today/reports/book_covers_preview_Compatibility_Report.webp"
import PalmistryCover from "@/assets/img/today/reports/book_covers_preview_Palmistry_Report.webp"
import SoulmateCover from "@/assets/img/today/reports/book_covers_preview_Soulmate_Sketch.webp"
import AstroMapCover from "@/assets/img/today/reports/book_covers_preview_Astro_Map.webp"
import AstrologyHuge from "@/assets/img/today/reports/book_covers/book_covers_preview_huge_astrology_report_2x.webp"
import LoveMatchHuge from "@/assets/img/today/reports/book_covers/book_covers_preview_huge_love_match_report_2x.webp"
import CompatibilityHuge from "@/assets/img/today/reports/book_covers/book_covers_preview_huge_compatibility_report_2x.webp"
import PalmistryHuge from "@/assets/img/today/reports/book_covers/book_covers_preview_huge_palmistry_report_2x.webp"
import SoulmateHuge from "@/assets/img/today/reports/book_covers/book_covers_preview_huge_soulmate_sketch_2x.webp"
import AstroMapHuge from "@/assets/img/today/reports/book_covers/book_covers_preview_huge_astro_map_2x.webp"
import AstrologySliderCover from "@/assets/img/today/reports/slider_covers/book_covers_preview_medium_astrology.webp"
import LoveMatchSliderCover from "@/assets/img/today/reports/slider_covers/book_covers_preview_medium_love.webp"
import AstroMapSliderCover from "@/assets/img/today/reports/slider_covers/book_covers_preview_medium_astro_map.webp"
import SoulmateSliderCover from "@/assets/img/today/reports/slider_covers/book_covers_preview_medium_soulmate_sketch.webp"
import AuraScanCover from "@/assets/img/today/reports/book_covers_preview_Aura_scan.webp"
import AuraScanHuge from "@/assets/img/today/reports/book_covers/book_covers_preview_huge_aura_scan_report_2x.webp"
import AuraScanSliderCover from "@/assets/img/today/reports/slider_covers/book_covers_preview_medium_aura_scan.webp"

import { WebappRoutes, type WebappRoute } from "@/shared/lib/webapp-routes"

export type ReportPurchaseType = "star" | "truths" | "always" | "soulmate" | "astromap" | "aura"

/** How a purchased report is delivered to the user. */
export type ReportDeliveryType = "email" | "download" | "in-app"

/**
 * Stable, machine identifier for a report — the canonical key used in the
 * `?report=` URL param, PDF resolution, unlock/reveal storage and (soon) the
 * backend report `type` enum. NEVER the human `title`, which is display-only.
 * Keep these string values in sync with the backend report-type enum.
 *
 * Today: `astrology` and `love-match` are the PDF reports (shown in the report
 * viewer); `compatibility` and `palmistry` are in-app pages (no PDF).
 * `astro-map`, `soulmate-sketch`, and `aura-scan` are purchasable reports.
 */
export const ReportIds = {
  Astrology: "astrology",
  LoveMatch: "love-match",
  Compatibility: "compatibility",
  Palmistry: "palmistry",
  Soulmate: "soulmate-sketch",
  AstroMap: "astro-map",
  AuraScan: "aura-scan",
} as const

export type ReportId = (typeof ReportIds)[keyof typeof ReportIds]

export interface ReportPurchaseFlags {
  isStarReportPurchased: boolean
  isTruthsReportPurchased: boolean
  isSoulmateReportPurchased: boolean
  isAstroMapReportPurchased: boolean
  isAuraScanReportPurchased: boolean
}

export interface ReportItem {
  /** Stable identity (see {@link ReportId}); used everywhere except display. */
  id: ReportId
  title: string
  description: string
  cover: string
  /** Large portrait cover shown on the Reports tab cards. */
  hugeCover: string
  /** Medium tilted cover used by the Readings promo slider. */
  sliderCover?: string
  rating: number
  purchaseCount: string
  /** Price shown on the compact cross-sell list. */
  price: number
  /** Which entitlement controls ownership state for this report. */
  purchaseType: ReportPurchaseType
  /** Post-purchase delivery behavior for this report. */
  delivery: ReportDeliveryType
  /** When the report maps to an existing webapp page, link straight to it. */
  route?: WebappRoute
}

/** Personalized report catalog. Ported from lpr-web's WebAppProvider reports list. */
export const REPORTS: ReportItem[] = [
  {
    id: ReportIds.Astrology,
    title: "Astrology Report",
    description:
      "A deep dive into your birth chart — your strengths, blind spots, and the year ahead.",
    cover: AstrologyCover,
    hugeCover: AstrologyHuge,
    sliderCover: AstrologySliderCover,
    rating: 4.7,
    purchaseCount: "34,267",
    price: 19,
    purchaseType: "star",
    delivery: "email",
  },
  {
    id: ReportIds.LoveMatch,
    title: "Love Match Report",
    description: "What your chart reveals about how you love, who fits you, and what to watch for.",
    cover: LoveMatchCover,
    hugeCover: LoveMatchHuge,
    sliderCover: LoveMatchSliderCover,
    rating: 4.8,
    purchaseCount: "12,357",
    price: 15,
    purchaseType: "truths",
    delivery: "download",
  },
  {
    id: ReportIds.AstroMap,
    title: "Astro Map",
    description:
      "Discover the places on Earth where your stars align for love, career, and growth.",
    cover: AstroMapCover,
    hugeCover: AstroMapHuge,
    sliderCover: AstroMapSliderCover,
    rating: 4.9,
    purchaseCount: "16,214",
    price: 19,
    purchaseType: "astromap",
    delivery: "download",
  },
  {
    id: ReportIds.Soulmate,
    title: "Soulmate Sketch",
    description: "A guided sketch and reading of the person your stars are drawing toward you.",
    cover: SoulmateCover,
    hugeCover: SoulmateHuge,
    sliderCover: SoulmateSliderCover,
    rating: 4.8,
    purchaseCount: "24,947",
    price: 29,
    purchaseType: "soulmate",
    delivery: "in-app",
    route: WebappRoutes.SOULMATE,
  },
  {
    id: ReportIds.AuraScan,
    title: "Aura Scan Report",
    description:
      "Reveal the colors, energy patterns, and qualities that make your aura uniquely yours.",
    cover: AuraScanCover,
    hugeCover: AuraScanHuge,
    sliderCover: AuraScanSliderCover,
    rating: 4.8,
    purchaseCount: "18,420",
    price: 19,
    purchaseType: "aura",
    delivery: "in-app",
    route: WebappRoutes.AURA_SCAN,
  },
  {
    id: ReportIds.Compatibility,
    title: "Compatibility Report",
    description:
      "Check how your sign pairs with any other — chemistry, friction, and the full breakdown.",
    cover: CompatibilityCover,
    hugeCover: CompatibilityHuge,
    rating: 4.9,
    purchaseCount: "297,365",
    price: 13,
    purchaseType: "always",
    delivery: "in-app",
    route: WebappRoutes.COMPATIBILITY,
  },
  {
    id: ReportIds.Palmistry,
    title: "Palmistry Report",
    description:
      "Your palm lines, decoded — life, heart, head, and the marks that shape your path.",
    cover: PalmistryCover,
    hugeCover: PalmistryHuge,
    rating: 4.8,
    purchaseCount: "297,365",
    price: 15,
    purchaseType: "always",
    delivery: "in-app",
    route: WebappRoutes.PALMISTRY,
  },
]

/** Narrow an unknown value (e.g. a raw URL search param) to a known `ReportId`. */
export function isReportId(value: unknown): value is ReportId {
  return typeof value === "string" && (Object.values(ReportIds) as string[]).includes(value)
}

/** Look up a report by its stable id. */
export function getReportById(id: ReportId): ReportItem | undefined {
  return REPORTS.find((r) => r.id === id)
}

export function isReportPurchased(report: ReportItem, flags: ReportPurchaseFlags): boolean {
  switch (report.purchaseType) {
    case "star":
      return flags.isStarReportPurchased
    case "truths":
      return flags.isTruthsReportPurchased
    case "soulmate":
      return flags.isSoulmateReportPurchased
    case "astromap":
      return flags.isAstroMapReportPurchased
    case "aura":
      return flags.isAuraScanReportPurchased
    case "always":
      return true
    default:
      return false
  }
}
